import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Loader2, ClipboardList, Shield, Download } from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchAuditLogs } from '../services/adminService'

interface AuditEntry {
  id: string
  adminName: string
  adminEmail?: string
  action: string
  target: string
  details: string
  createdAt: string
}

const ACTION_TABS = [
  { value: 'ALL', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'LOGIN', label: 'Login' },
]

const ACTION_STYLES: Record<string, { bg: string; text: string }> = {
  CREATE: { bg: 'bg-green-100', text: 'text-green-700' },
  UPDATE: { bg: 'bg-blue-100', text: 'text-blue-700' },
  DELETE: { bg: 'bg-red-100', text: 'text-red-700' },
  REFUND: { bg: 'bg-purple-100', text: 'text-purple-700' },
  LOGIN: { bg: 'bg-gray-100', text: 'text-gray-600' },
}

const PAGE_SIZE = 15

function exportToCsv(headers: string[], rows: string[][], filename: string) {
  const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function AuditLog() {
  const [logs, setLogs] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [actionFilter, setActionFilter] = useState('ALL')
  const [page, setPage] = useState(1)

  const loadLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { page, limit: PAGE_SIZE }
      if (actionFilter !== 'ALL') params.action = actionFilter

      const data = await fetchAuditLogs(params as Parameters<typeof fetchAuditLogs>[0])
      setLogs(data?.logs ?? [])
      setTotal(data?.pagination?.total ?? 0)
      setTotalPages(data?.pagination?.totalPages ?? 0)
    } catch {
      toast.error('Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }, [actionFilter, page])

  useEffect(() => { loadLogs() }, [loadLogs])

  const handleExport = () => {
    if (logs.length === 0) {
      toast.error('No logs to export')
      return
    }
    const headers = ['Date', 'Admin', 'Email', 'Action', 'Target', 'Details']
    const rows = logs.map((l) => [
      new Date(l.createdAt).toLocaleString(),
      l.adminName,
      l.adminEmail ?? '',
      l.action,
      l.target,
      l.details,
    ])
    exportToCsv(headers, rows, `audit-log-${new Date().toISOString().slice(0, 10)}.csv`)
    toast.success('Audit log exported')
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Entries', value: total, icon: ClipboardList, color: 'text-blue-600 bg-blue-50' },
          { label: 'Filtered', value: actionFilter === 'ALL' ? 'All' : actionFilter, icon: Shield, color: 'text-green-700 bg-green-50' },
          { label: 'Page', value: `${page} / ${totalPages || 1}`, icon: ClipboardList, color: 'text-yellow-700 bg-yellow-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs + Export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {ACTION_TABS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => { setActionFilter(value); setPage(1) }}
              className={`px-4 py-2 text-xs font-semibold transition-colors ${
                actionFilter === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-[#6B7280] hover:bg-gray-50 shadow-sm transition-colors"
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} log entr{total !== 1 ? 'ies' : 'y'}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Date', 'Admin', 'Action', 'Target', 'Details'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {logs.map((entry) => {
                  const style = ACTION_STYLES[entry.action] ?? { bg: 'bg-gray-100', text: 'text-gray-600' }
                  return (
                    <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium text-[#1A1A2E]">{entry.adminName}</p>
                        {entry.adminEmail && (
                          <p className="text-[10px] text-[#6B7280]">{entry.adminEmail}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                          {entry.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs font-medium text-[#1A1A2E] max-w-[200px] truncate">
                        {entry.target}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[300px] truncate" title={entry.details}>
                        {entry.details}
                      </td>
                    </tr>
                  )
                })}
                {logs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No audit log entries found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}&ndash;{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setPage(pageNum)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                      pageNum === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
